import '@/entrypoints/application.css';
import '@/lib/i18n';

import { createInertiaApp } from '@inertiajs/react';
import { createRoot } from 'react-dom/client';

import { ErrorBoundary } from '@/components/error-boundary';
import { NetworkBanner } from '@/components/network-banner';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/ui/sonner';
import { initializeAnalytics } from '@/lib/analytics';
import { hasNativeBridge, initCapacitor } from '@/lib/capacitor';
import { config } from '@/lib/config';
import { resolveInertiaPage } from '@/lib/inertia-page-resolver';
import { initializePushNotifications } from '@/lib/push-notifications';
import { redirectInstalledWebApp } from '@/lib/pwa-launch';
import Sentry, { initializeSentry } from '@/lib/sentry';

const pages = import.meta.glob('../pages/**/*.jsx');

const { pwaLaunchPath, pwaRootPath } = document.documentElement.dataset;
const redirectedFromInstalledRoot = redirectInstalledWebApp({
  currentPath: window.location.pathname,
  rootPath: pwaRootPath,
  launchPath: pwaLaunchPath,
  navigatorStandalone: window.navigator.standalone === true,
  displayModeStandalone: window.matchMedia('(display-mode: standalone)')
    .matches,
  replace: (path) => window.location.replace(path),
});

if (!redirectedFromInstalledRoot) {
  void initializeSentry();

  createInertiaApp({
    progress: {
      color: '#6552D9',
      showSpinner: false,
    },
    resolve: (name) => resolveInertiaPage(pages, name),
    setup({ el, App, props }) {
      createRoot(el).render(
        <ErrorBoundary>
          <ThemeProvider>
            <App {...props} />
            <NetworkBanner />
            <Toaster richColors closeButton position="top-right" />
          </ThemeProvider>
        </ErrorBoundary>,
      );

      void initCapacitor({
        entryPath: props.initialPage.props.routes.app,
      })
        .then(async (runtime) => {
          const nativeBridge = hasNativeBridge(runtime);
          await initializeAnalytics({
            consent: props.initialPage.props.telemetry.analytics_enabled,
            native: nativeBridge,
            config: config.analytics.firebase,
          });
          if (nativeBridge) {
            await initializePushNotifications({
              url: props.initialPage.props.routes.push_token,
            });
          }
        })
        .catch((error) => Sentry.captureException(error));
    },
  });
}
